import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  InputAdornment,
  IconButton,
  Paper,
  Stack,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person as PersonIcon,
  Lock as LockIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { signIn } from 'aws-amplify/auth';

interface LoginProps {
  onLogin: () => void;
}

export default function Login({ onLogin }: LoginProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Añadir prefijo fact_ al username
      const cognitoUsername = `fact_${username}`;
      await signIn({ username: cognitoUsername, password });
      onLogin();
    } catch (err: any) {
      setError(err.message || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 50%, #1e293b 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.05)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -150,
          left: -150,
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.03)',
        }}
      />

      {/* Logo Kreando en la esquina superior izquierda */}
      <Box
        sx={{
          position: 'absolute',
          top: 24,
          left: 24,
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          component="img"
          src="/kreando-logo.png"
          alt="Kreando Software"
          sx={{
            height: 50,
            filter: 'brightness(0) invert(1)',
            opacity: 0.9,
          }}
          onError={(e: any) => {
            // Si no se encuentra la imagen, ocultar el elemento
            e.target.style.display = 'none';
          }}
        />
      </Box>

      {/* Badges en la esquina superior derecha */}
      <Box
        sx={{
          position: 'absolute',
          top: 24,
          right: 24,
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          alignItems: 'flex-end',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            px: 2,
            py: 0.5,
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#FF9900' }}>
            Powered by AWS
          </Typography>
        </Paper>
        <Paper
          elevation={3}
          sx={{
            px: 2,
            py: 0.5,
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 2,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af' }}>
            Integrado con SUNAT
          </Typography>
        </Paper>
      </Box>

      <Container maxWidth="sm" sx={{ display: 'flex', alignItems: 'center', position: 'relative', zIndex: 1 }}>
        <Card
          elevation={24}
          sx={{
            width: '100%',
            borderRadius: 4,
            overflow: 'hidden',
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
          }}
        >
          {/* Header con color */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
              py: 4,
              px: 3,
              textAlign: 'center',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.15)',
                mb: 2,
                backdropFilter: 'blur(10px)',
              }}
            >
              <ReceiptIcon sx={{ fontSize: 48, color: 'white' }} />
            </Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                color: 'white',
                fontWeight: 700,
                mb: 1,
                textShadow: '0 2px 4px rgba(0,0,0,0.2)',
              }}
            >
              Sistema de Facturación Electrónica
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.95rem',
              }}
            >
              Gestión de Comprobantes SUNAT
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            <Typography
              variant="h6"
              align="center"
              sx={{ mb: 3, color: 'text.secondary', fontWeight: 500 }}
            >
              Iniciar Sesión
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Usuario"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoFocus
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
                <TextField
                  fullWidth
                  label="Contraseña"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                    boxShadow: '0 4px 12px rgba(30, 64, 175, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1e3a8a 0%, #1e293b 100%)',
                      boxShadow: '0 6px 16px rgba(30, 64, 175, 0.4)',
                    },
                  }}
                >
                  {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
                </Button>
              </Stack>
            </form>
          </CardContent>

          {/* Footer */}
          <Box
            sx={{
              py: 2.5,
              px: 3,
              background: 'rgba(30, 64, 175, 0.03)',
              borderTop: '1px solid rgba(30, 64, 175, 0.1)',
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mb: 1 }}>
              <Box
                component="img"
                src="/kreando-logo.png"
                alt="Kreando Software"
                sx={{
                  height: 32,
                  opacity: 0.8,
                }}
                onError={(e: any) => {
                  e.target.style.display = 'none';
                }}
              />
            </Stack>
            <Typography
              variant="caption"
              align="center"
              display="block"
              sx={{ color: 'text.secondary', fontSize: '0.85rem' }}
            >
              Desarrollado por{' '}
              <Box
                component="span"
                sx={{
                  color: 'primary.main',
                  fontWeight: 700,
                }}
              >
                Kreando Software SAC
              </Box>
            </Typography>
            <Typography
              variant="caption"
              align="center"
              display="block"
              sx={{ color: 'text.secondary', fontSize: '0.75rem', mt: 0.5 }}
            >
              © {new Date().getFullYear()} - Todos los derechos reservados
            </Typography>
          </Box>
        </Card>
      </Container>
    </Box>
  );
}
